import { useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import type { TFunction } from 'i18next'

import {
  CampaignChannelBadges,
  CampaignOwnerCell,
  CampaignStatusPill,
} from '#/entities/campaign'
import type { CampaignMetric, CampaignRow } from '#/entities/campaign'
import { CampaignKind } from '#/shared/api/generated/communication/models'
import { cn } from '#/shared/lib/utils'
import type { DataTableColumn } from '#/shared/ui/data-table'

import type { CampaignRowActions } from '../model/use-campaign-row-actions'
import { CampaignRowMenu } from './campaign-row-menu'

const EM_DASH = '—'

/** A right-aligned figure with an optional quieter line under it. */
function MetricCell({
  metric,
  noteClassName,
}: {
  metric: CampaignMetric
  noteClassName?: string
}) {
  if (metric.value == null) {
    return <span className="block text-sm text-gray-900">{EM_DASH}</span>
  }
  return (
    <span className="flex flex-col items-end gap-1">
      <span className="text-sm font-medium text-announce-ink">
        {metric.value}
      </span>
      {metric.note != null && (
        <span className={cn('text-xs text-gray-500', noteClassName)}>
          {metric.note}
        </span>
      )}
    </span>
  )
}

/** A sequence is the default and carries no tag; the other two say what
 *  they are, automation in the amber the "Automation" owner already wears. */
const KIND_TAG_CLASS: Partial<Record<CampaignKind, string>> = {
  [CampaignKind.automation]: 'bg-avatar-amber-soft text-avatar-amber',
  [CampaignKind.broadcast]: 'bg-gray-100 text-gray-700',
}

function KindTag({ kind }: { kind: CampaignKind }) {
  const { t } = useTranslation()
  const tone = KIND_TAG_CLASS[kind]
  if (tone === undefined) return null
  return (
    <span
      className={cn(
        'shrink-0 rounded-md px-1.5 py-0.5 text-2xs font-medium',
        tone,
      )}
    >
      {t(`campaigns.kind.${kind}`)}
    </span>
  )
}

function campaignColumn(t: TFunction): DataTableColumn<CampaignRow> {
  return {
    key: 'campaign',
    header: t('campaigns.columns.campaign'),
    className: 'min-w-57',
    render: (row) => (
      <span className="flex flex-col gap-1">
        <span className="flex items-center gap-2">
          <span className="truncate text-sm font-medium text-gray-900">
            {row.name}
          </span>
          <KindTag kind={row.kind} />
          <CampaignChannelBadges channels={row.channels} />
        </span>
        <span className="truncate text-xs text-blue-500">{row.audience}</span>
      </span>
    ),
  }
}

function statusColumn(t: TFunction): DataTableColumn<CampaignRow> {
  return {
    key: 'status',
    header: t('campaigns.columns.status'),
    className: 'w-26',
    render: (row) => <CampaignStatusPill status={row.status} />,
  }
}

function peopleColumn(t: TFunction): DataTableColumn<CampaignRow> {
  return {
    key: 'people',
    header: t('campaigns.columns.people'),
    className: 'w-19 text-right',
    render: (row) => (
      <span className="flex flex-col items-end gap-1">
        <span className="text-sm text-gray-900">{row.people.value}</span>
        {row.people.note != null && (
          <span className="text-xs text-gray-500">{row.people.note}</span>
        )}
      </span>
    ),
  }
}

function waitingColumn(t: TFunction): DataTableColumn<CampaignRow> {
  return {
    key: 'waiting',
    header: t('campaigns.columns.waitingOnUs'),
    className: 'w-29.5',
    render: (row) => (
      <span
        className={cn(
          'text-xs',
          row.waitingOverdue
            ? 'font-medium text-campaign-attention'
            : 'text-gray-500',
        )}
      >
        {row.waitingOnUs ?? EM_DASH}
      </span>
    ),
  }
}

function repliesColumn(t: TFunction): DataTableColumn<CampaignRow> {
  return {
    key: 'replies',
    header: t('campaigns.columns.replies'),
    className: 'w-19 text-right',
    render: (row) => (
      <span className="text-sm text-gray-900">{row.replies ?? EM_DASH}</span>
    ),
  }
}

function bookedColumn(t: TFunction): DataTableColumn<CampaignRow> {
  return {
    key: 'booked',
    header: t('campaigns.columns.booked'),
    className: 'w-21 text-right',
    render: (row) => <MetricCell metric={row.booked} />,
  }
}

function marginColumn(t: TFunction): DataTableColumn<CampaignRow> {
  return {
    key: 'margin',
    header: t('campaigns.columns.margin'),
    className: 'w-21 text-right',
    render: (row) => (
      <MetricCell metric={row.margin} noteClassName="text-warm-500" />
    ),
  }
}

function ownerColumn(t: TFunction): DataTableColumn<CampaignRow> {
  return {
    key: 'owner',
    header: t('campaigns.columns.owner'),
    className: 'w-28',
    render: (row) => <CampaignOwnerCell owner={row.owner} />,
  }
}

function lastSendColumn(t: TFunction): DataTableColumn<CampaignRow> {
  return {
    key: 'lastSend',
    header: t('campaigns.columns.lastSend'),
    className: 'w-23',
    render: (row) => (
      <span className="text-xs text-gray-500">{row.lastSend ?? EM_DASH}</span>
    ),
  }
}

/** The menu sits inside a clickable row, so the cell swallows the click that
 *  opens it — otherwise every menu opening also navigates to the campaign. */
function actionsColumn(
  actions: CampaignRowActions,
): DataTableColumn<CampaignRow> {
  return {
    key: 'actions',
    header: '',
    className: 'w-10',
    render: (row) => (
      <div onClick={(event) => event.stopPropagation()}>
        <CampaignRowMenu row={row} actions={actions} />
      </div>
    ),
  }
}

export function useCampaignColumns(
  actions: CampaignRowActions,
): DataTableColumn<CampaignRow>[] {
  const { t } = useTranslation()
  return useMemo(
    () => [
      campaignColumn(t),
      statusColumn(t),
      peopleColumn(t),
      waitingColumn(t),
      repliesColumn(t),
      bookedColumn(t),
      marginColumn(t),
      ownerColumn(t),
      lastSendColumn(t),
      actionsColumn(actions),
    ],
    [t, actions],
  )
}
